import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MapPin,
  Plus,
  Search,
  Download,
  Edit,
  Trash2,
  Eye,
  Building,
  Layers,
  Home,
  DoorOpen,
  X,
  Check,
  ChevronRight,
  FileSpreadsheet,
  FileText,
  ChevronDown
} from 'lucide-react';
import { branchService } from '../../../services/branchService';
import { locationService } from '../../../services/locationService';
import { exportToCSV, exportToExcel } from '../../../utils/exportUtils';
import UsageMeter from '../../../components/UsageMeter';
import OverageConsentModal from '../../../components/OverageConsentModal';
import { subscriptionService } from '../../../services/subscriptionService';

const Locations = () => {
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState('table');
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [filterBranch, setFilterBranch] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [locations, setLocations] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [viewingLocation, setViewingLocation] = useState(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [cmmsSubscription, setCmmsSubscription] = useState(null);
  const [showOverageModal, setShowOverageModal] = useState(false);
  const [formData, setFormData] = useState({
    branchId: '',
    parentLocationId: '',
    code: '',
    name: '',
    type: 'ROOM',
    address: '',
    city: '',
    state: '',
    country: '',
    postalCode: '',
    description: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    subscriptionService.getCmmsSubscription()
      .then(data => setCmmsSubscription(data))
      .catch(() => {});
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [branchesData, locationsData] = await Promise.all([
        branchService.getBranches(),
        locationService.getLocations()
      ]);
      setBranches(branchesData || []);
      setLocations(locationsData || []);
    } catch (error) {
      console.error('Error loading data:', error);
      setBranches([]);
      setLocations([]);
    } finally {
      setLoading(false);
    }
  };

  const getTypeIcon = (type) => {
    const icons = {
      REGION: MapPin,
      WAREHOUSE: Building,
      FACTORY: Building,
      OFFICE: Home,
      BUILDING: Building,
      FLOOR: Layers,
      ROOM: DoorOpen
    };
    return icons[type] || MapPin;
  };

  const getTypeLabel = (type) => {
    const labels = {
      REGION: t('cmms.loc_region'),
      WAREHOUSE: t('cmms.loc_warehouse'),
      FACTORY: t('cmms.loc_factory'),
      OFFICE: t('cmms.loc_office'),
      BUILDING: t('cmms.loc_building'),
      FLOOR: t('cmms.loc_floor'),
      ROOM: t('cmms.loc_room')
    };
    return labels[type] || type;
  };

  const getTypeColor = (type) => {
    const colors = {
      REGION: 'bg-purple-100 text-purple-600',
      WAREHOUSE: 'bg-orange-100 text-orange-600',
      FACTORY: 'bg-blue-100 text-blue-600',
      OFFICE: 'bg-green-100 text-green-600',
      BUILDING: 'bg-indigo-100 text-indigo-600',
      FLOOR: 'bg-yellow-100 text-yellow-600',
      ROOM: 'bg-pink-100 text-pink-600'
    };
    return colors[type] || 'bg-gray-100 text-gray-600';
  };

  const filteredLocations = locations.filter(location => {
    const matchesBranch = filterBranch === 'all' || location.branchId === parseInt(filterBranch);
    const matchesType = filterType === 'all' || location.type === filterType;
    const matchesSearch =
      location.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      location.code.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesBranch && matchesType && matchesSearch;
  });

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedLocations(filteredLocations.map(l => l.id));
    } else {
      setSelectedLocations([]);
    }
  };

  const handleSelect = (id) => {
    setSelectedLocations(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleCreate = () => {
    setEditingLocation(null);
    setFormData({
      branchId: '',
      parentLocationId: '',
      code: '',
      name: '',
      type: 'ROOM',
      address: '',
      city: '',
      state: '',
      country: '',
      postalCode: '',
      description: ''
    });
    const current = locations.length;
    const max = cmmsSubscription?.includedLocations ?? null;
    if (max !== null && current >= max) {
      setShowOverageModal(true);
    } else {
      setShowModal(true);
    }
  };

  const handleEdit = (location) => {
    setEditingLocation(location);
    setFormData({
      branchId: location.branchId || '',
      parentLocationId: location.parentLocationId || '',
      code: location.code,
      name: location.name,
      type: location.type,
      address: location.address || '',
      city: location.city || '',
      state: location.state || '',
      country: location.country || '',
      postalCode: location.postalCode || '',
      description: location.description || ''
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingLocation) {
        await locationService.updateLocation(editingLocation.id, formData);
      } else {
        await locationService.createLocation(formData);
        subscriptionService.getCmmsSubscription()
          .then(data => setCmmsSubscription(data))
          .catch(() => {});
      }
      setShowModal(false);
      loadData();
    } catch (error) {
      console.error('Error saving location:', error);
      alert('Failed to save location. Please try again.');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this location?')) {
      try {
        await locationService.deleteLocation(id);
        loadData();
      } catch (error) {
        console.error('Error deleting location:', error);
      }
    }
  };

  // Get locations for parent dropdown (filter by selected branch)
  const getParentLocationOptions = () => {
    if (!formData.branchId) return [];
    return locations.filter(l =>
      l.branchId === parseInt(formData.branchId) &&
      (!editingLocation || l.id !== editingLocation.id)
    );
  };

  // Stats
  const stats = {
    total: locations.length,
    buildings: locations.filter(l => l.type === 'BUILDING').length,
    rooms: locations.filter(l => l.type === 'ROOM').length,
    totalAssets: locations.reduce((sum, l) => sum + (l.assetCount || 0), 0)
  };

  // Export handler
  const handleExport = (format) => {
    const data = filteredLocations.map(l => ({
      code: l.code || '',
      name: l.name || '',
      type: getTypeLabel(l.type),
      branchName: l.branchName || '',
      branchCode: l.branchCode || '',
      parentLocation: l.parentLocationName || 'Root',
      address: l.address || '',
      city: l.city || '',
      state: l.state || '',
      country: l.country || '',
      postalCode: l.postalCode || '',
      description: l.description || '',
      assetCount: l.assetCount || 0,
      childCount: l.childCount || 0
    }));

    const columns = [
      { header: 'Location Code', key: 'code' },
      { header: 'Name', key: 'name' },
      { header: 'Type', key: 'type' },
      { header: 'Branch', key: 'branchName' },
      { header: 'Branch Code', key: 'branchCode' },
      { header: 'Parent Location', key: 'parentLocation' },
      { header: 'Address', key: 'address' },
      { header: 'City', key: 'city' },
      { header: 'State', key: 'state' },
      { header: 'Country', key: 'country' },
      { header: 'Postal Code', key: 'postalCode' },
      { header: 'Description', key: 'description' },
      { header: 'Assets Count', key: 'assetCount' },
      { header: 'Sub-locations Count', key: 'childCount' }
    ];

    const filename = `locations_export_${new Date().toISOString().split('T')[0]}`;

    if (format === 'csv') {
      exportToCSV(data, columns, filename);
    } else {
      exportToExcel(data, columns, filename, 'Locations');
    }
    setShowExportMenu(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('cmms.loc_title')}</h1>
          <p className="text-gray-600 mt-1">{t('cmms.loc_subtitle')}</p>
        </div>
        <div className="flex gap-3">
          <div className="relative">
            <button
              onClick={() => setShowExportMenu(!showExportMenu)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50"
            >
              <Download className="w-5 h-5 mr-2" />
              Export
              <ChevronDown className="w-4 h-4 ml-2" />
            </button>
            {showExportMenu && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
                <button
                  onClick={() => handleExport('excel')}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 rounded-t-lg"
                >
                  <FileSpreadsheet className="w-4 h-4 text-green-600" />
                  Export to Excel
                </button>
                <button
                  onClick={() => handleExport('csv')}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 rounded-b-lg"
                >
                  <FileText className="w-4 h-4 text-blue-600" />
                  Export to CSV
                </button>
              </div>
            )}
          </div>
          <button
            onClick={handleCreate}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-5 h-5 mr-2" />
            {t('cmms.loc_addLocation')}
          </button>
        </div>
      </div>

      {cmmsSubscription && (
        <UsageMeter
          label={t('cmms.locations')}
          current={locations.length}
          max={cmmsSubscription.includedLocations ?? null}
          className="mb-4"
        />
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <MapPin className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
              <div className="text-sm text-gray-600">{t('cmms.loc_totalLocations')}</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <Building className="w-6 h-6 text-indigo-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{stats.buildings}</div>
              <div className="text-sm text-gray-600">{t('cmms.loc_buildings')}</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-pink-100 rounded-lg">
              <DoorOpen className="w-6 h-6 text-pink-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{stats.rooms}</div>
              <div className="text-sm text-gray-600">{t('cmms.loc_rooms')}</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Check className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{stats.totalAssets}</div>
              <div className="text-sm text-gray-600">{t('cmms.loc_assetsInLocations')}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-64">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder={t('cmms.loc_searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <select
            value={filterBranch}
            onChange={(e) => setFilterBranch(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">{t('cmms.loc_allBranches')}</option>
            {branches.map(branch => (
              <option key={branch.id} value={branch.id}>
                {branch.name}
              </option>
            ))}
          </select>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">{t('cmms.loc_allTypes')}</option>
            <option value="REGION">{t('cmms.loc_region')}</option>
            <option value="WAREHOUSE">{t('cmms.loc_warehouse')}</option>
            <option value="FACTORY">{t('cmms.loc_factory')}</option>
            <option value="OFFICE">{t('cmms.loc_office')}</option>
            <option value="BUILDING">{t('cmms.loc_building')}</option>
            <option value="FLOOR">{t('cmms.loc_floor')}</option>
            <option value="ROOM">{t('cmms.loc_room')}</option>
          </select>
          <div className="flex border border-gray-300 rounded-lg overflow-hidden">
            <button
              onClick={() => setViewMode('table')}
              className={`px-3 py-2 ${viewMode === 'table' ? 'bg-blue-50 text-blue-600' : 'bg-white text-gray-600'}`}
            >
              {t('cmms.loc_table')}
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={`px-3 py-2 ${viewMode === 'grid' ? 'bg-blue-50 text-blue-600' : 'bg-white text-gray-600'}`}
            >
              {t('cmms.loc_grid')}
            </button>
          </div>
        </div>
      </div>

      {/* Table View */}
      {viewMode === 'table' ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    onChange={handleSelectAll}
                    checked={selectedLocations.length === filteredLocations.length && filteredLocations.length > 0}
                    className="rounded border-gray-300"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('cmms.loc_title')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('common.type')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('common.branch')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('cmms.loc_parentLocation')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('cmms.loc_assetsInLocations')}</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredLocations.map((location) => {
                const TypeIcon = getTypeIcon(location.type);
                return (
                  <tr key={location.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedLocations.includes(location.id)}
                        onChange={() => handleSelect(location.id)}
                        className="rounded border-gray-300"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center mr-3 ${getTypeColor(location.type)}`}>
                          <TypeIcon className="w-5 h-5" />
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{location.name}</div>
                          <div className="text-sm text-gray-500">{location.code}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getTypeColor(location.type)}`}>
                        {getTypeLabel(location.type)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">
                        <div className="text-gray-900">{location.branchName}</div>
                        <div className="text-gray-500">{location.branchCode}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {location.parentLocationName ? (
                        <div className="flex items-center text-sm text-gray-600">
                          <ChevronRight className="w-4 h-4 mr-1" />
                          {location.parentLocationName}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">{t('cmms.loc_rootLocation')}</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {location.assetCount || 0} assets
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => setViewingLocation(location)} className="p-1 text-gray-400 hover:text-blue-600">
                          <Eye className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleEdit(location)}
                          className="p-1 text-gray-400 hover:text-blue-600"
                        >
                          <Edit className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(location.id)}
                          className="p-1 text-gray-400 hover:text-red-600"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        /* Grid View */
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredLocations.map((location) => {
            const TypeIcon = getTypeIcon(location.type);
            return (
              <div key={location.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${getTypeColor(location.type)}`}>
                      <TypeIcon className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{location.name}</h3>
                      <p className="text-sm text-gray-500">{location.code}</p>
                    </div>
                  </div>
                  <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getTypeColor(location.type)}`}>
                    {getTypeLabel(location.type)}
                  </span>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Building className="w-4 h-4" />
                    {location.branchName}
                  </div>
                  {location.parentLocationName && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <ChevronRight className="w-4 h-4" />
                      {t('cmms.loc_inside')}: {location.parentLocationName}
                    </div>
                  )}
                  {location.city && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <MapPin className="w-4 h-4" />
                      {location.city}, {location.state}
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-4 py-3 border-t border-b border-gray-100 mb-4">
                  <div className="text-center flex-1">
                    <div className="text-lg font-semibold text-gray-900">{location.assetCount || 0}</div>
                    <div className="text-xs text-gray-500">{t('cmms.loc_assetsInLocations')}</div>
                  </div>
                  <div className="text-center flex-1">
                    <div className="text-lg font-semibold text-gray-900">{location.childCount || 0}</div>
                    <div className="text-xs text-gray-500">{t('cmms.loc_subLocations')}</div>
                  </div>
                </div>

                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => handleEdit(location)}
                    className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                  >
                    <Edit className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(location.id)}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* View Modal */}
      {viewingLocation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center gap-3">
                {(() => { const TypeIcon = getTypeIcon(viewingLocation.type); return (
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getTypeColor(viewingLocation.type)}`}>
                    <TypeIcon className="w-5 h-5" />
                  </div>
                ); })()}
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">{viewingLocation.name}</h2>
                  <p className="text-sm text-gray-500">{viewingLocation.code}</p>
                </div>
              </div>
              <button onClick={() => setViewingLocation(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs font-medium text-gray-500 uppercase mb-1">{t('common.type')}</div>
                  <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getTypeColor(viewingLocation.type)}`}>
                    {getTypeLabel(viewingLocation.type)}
                  </span>
                </div>
                <div>
                  <div className="text-xs font-medium text-gray-500 uppercase mb-1">{t('common.branch')}</div>
                  <div className="text-sm text-gray-900">{viewingLocation.branchName || '—'}</div>
                  <div className="text-xs text-gray-500">{viewingLocation.branchCode}</div>
                </div>
                <div>
                  <div className="text-xs font-medium text-gray-500 uppercase mb-1">{t('cmms.loc_parentLocation')}</div>
                  <div className="text-sm text-gray-900">{viewingLocation.parentLocationName || t('cmms.loc_rootLocation')}</div>
                </div>
                <div>
                  <div className="text-xs font-medium text-gray-500 uppercase mb-1">{t('cmms.loc_assetsInLocations')}</div>
                  <div className="text-sm text-gray-900">{viewingLocation.assetCount || 0}</div>
                </div>
                <div>
                  <div className="text-xs font-medium text-gray-500 uppercase mb-1">{t('cmms.loc_subLocations')}</div>
                  <div className="text-sm text-gray-900">{viewingLocation.childCount || 0}</div>
                </div>
              </div>
              {(viewingLocation.address || viewingLocation.city) && (
                <div className="border-t border-gray-100 pt-4">
                  <div className="text-xs font-medium text-gray-500 uppercase mb-2">{t('common.address')}</div>
                  <div className="text-sm text-gray-900">
                    {[viewingLocation.address, viewingLocation.city, viewingLocation.state, viewingLocation.postalCode, viewingLocation.country]
                      .filter(Boolean).join(', ')}
                  </div>
                </div>
              )}
              {viewingLocation.description && (
                <div className="border-t border-gray-100 pt-4">
                  <div className="text-xs font-medium text-gray-500 uppercase mb-2">{t('common.description')}</div>
                  <div className="text-sm text-gray-700">{viewingLocation.description}</div>
                </div>
              )}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  onClick={() => setViewingLocation(null)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={() => { setViewingLocation(null); handleEdit(viewingLocation); }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {t('common.edit')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingLocation ? t('cmms.loc_editLocation') : t('cmms.loc_addLocation')}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Branch Selection - Highlighted */}
              <div className="bg-blue-50 rounded-lg p-4">
                <label className="block text-sm font-medium text-blue-900 mb-2">
                  <Building className="w-4 h-4 inline mr-2" />
                  {t('common.branch')} *
                </label>
                <select
                  value={formData.branchId}
                  onChange={(e) => setFormData({...formData, branchId: e.target.value, parentLocationId: ''})}
                  className="w-full px-3 py-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                  required
                >
                  <option value="">{t('common.selectBranch')}</option>
                  {branches.map(branch => (
                    <option key={branch.id} value={branch.id}>
                      {branch.name} ({branch.code})
                    </option>
                  ))}
                </select>
                <p className="text-xs text-blue-600 mt-1">
                  {t('cmms.loc_branchRequired')}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.loc_locationCode')} *</label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({...formData, code: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                    placeholder={t('cmms.loc_phCode')}
                    disabled={editingLocation}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.name')} *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                    placeholder={t('cmms.loc_phName')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.loc_type')} *</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({...formData, type: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="REGION">{t('cmms.loc_region')}</option>
                    <option value="WAREHOUSE">{t('cmms.loc_warehouse')}</option>
                    <option value="FACTORY">{t('cmms.loc_factory')}</option>
                    <option value="OFFICE">{t('cmms.loc_office')}</option>
                    <option value="BUILDING">{t('cmms.loc_building')}</option>
                    <option value="FLOOR">{t('cmms.loc_floor')}</option>
                    <option value="ROOM">{t('cmms.loc_room')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.loc_parentLocation')}</label>
                  <select
                    value={formData.parentLocationId}
                    onChange={(e) => setFormData({...formData, parentLocationId: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    disabled={!formData.branchId}
                  >
                    <option value="">{t('cmms.loc_noParent')}</option>
                    {getParentLocationOptions().map(loc => (
                      <option key={loc.id} value={loc.id}>
                        {loc.name} ({loc.code})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Address Section */}
              <div className="border-t border-gray-200 pt-4 mt-4">
                <h3 className="text-sm font-medium text-gray-900 mb-3">{t('common.address')} ({t('common.optional')})</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.address')}</label>
                    <input
                      type="text"
                      value={formData.address}
                      onChange={(e) => setFormData({...formData, address: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.city')}</label>
                      <input
                        type="text"
                        value={formData.city}
                        onChange={(e) => setFormData({...formData, city: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.state')}</label>
                      <input
                        type="text"
                        value={formData.state}
                        onChange={(e) => setFormData({...formData, state: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.country')}</label>
                      <input
                        type="text"
                        value={formData.country}
                        onChange={(e) => setFormData({...formData, country: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.postalCode')}</label>
                      <input
                        type="text"
                        value={formData.postalCode}
                        onChange={(e) => setFormData({...formData, postalCode: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.description')}</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {editingLocation ? t('cmms.loc_updateLocation') : t('cmms.loc_createLocation')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <OverageConsentModal
        isOpen={showOverageModal}
        onClose={() => setShowOverageModal(false)}
        resourceLabel="location"
        current={locations.length}
        max={cmmsSubscription?.includedLocations ?? 0}
      >
        <div className="space-y-4">
          <div className="bg-blue-50 rounded-lg p-4">
            <label className="block text-sm font-medium text-blue-900 mb-2">
              <Building className="w-4 h-4 inline mr-2" />
              {t('common.branch')} *
            </label>
            <select
              value={formData.branchId}
              onChange={(e) => setFormData({...formData, branchId: e.target.value, parentLocationId: ''})}
              className="w-full px-3 py-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
              required
            >
              <option value="">{t('common.selectBranch')}</option>
              {branches.map(branch => (
                <option key={branch.id} value={branch.id}>
                  {branch.name} ({branch.code})
                </option>
              ))}
            </select>
            <p className="text-xs text-blue-600 mt-1">
              {t('cmms.loc_branchRequired')}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.loc_locationCode')} *</label>
              <input
                type="text"
                value={formData.code}
                onChange={(e) => setFormData({...formData, code: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
                placeholder="e.g., LOC-001"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.name')} *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
                placeholder="e.g., Building A"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.loc_type')} *</label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({...formData, type: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="REGION">{t('cmms.loc_region')}</option>
                <option value="WAREHOUSE">{t('cmms.loc_warehouse')}</option>
                <option value="FACTORY">{t('cmms.loc_factory')}</option>
                <option value="OFFICE">{t('cmms.loc_office')}</option>
                <option value="BUILDING">{t('cmms.loc_building')}</option>
                <option value="FLOOR">{t('cmms.loc_floor')}</option>
                <option value="ROOM">{t('cmms.loc_room')}</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.loc_parentLocation')}</label>
              <select
                value={formData.parentLocationId}
                onChange={(e) => setFormData({...formData, parentLocationId: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                disabled={!formData.branchId}
              >
                <option value="">{t('cmms.loc_noParent')}</option>
                {getParentLocationOptions().map(loc => (
                  <option key={loc.id} value={loc.id}>
                    {loc.name} ({loc.code})
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-4 mt-4">
            <h3 className="text-sm font-medium text-gray-900 mb-3">{t('common.address')} ({t('common.optional')})</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.address')}</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData({...formData, city: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                  <input
                    type="text"
                    value={formData.state}
                    onChange={(e) => setFormData({...formData, state: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                  <input
                    type="text"
                    value={formData.country}
                    onChange={(e) => setFormData({...formData, country: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Postal Code</label>
                  <input
                    type="text"
                    value={formData.postalCode}
                    onChange={(e) => setFormData({...formData, postalCode: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.description')}</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </OverageConsentModal>
    </div>
  );
};

export default Locations;
